import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppConsts } from '@shared/AppConsts';

export type RuleSet = {
    Required?: boolean;
    MaxLength?: number;
    MinLength?: number;
    Min?: number;
    Max?: number;
    Regex?: string;
    Pattern?: string;
    ValidationMessageId?: string;
    Section?: string;
    ValidationFunction?: string;
    [key: string]: any;
};

export type ValidationRulesMap = Record<string, RuleSet[]>;

const LS_PREFIX = 'valrules:';
const LS_TTL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ValidationRulesService {
    private cache = new Map<string, ValidationRulesMap>();

    constructor(private http: HttpClient) {}

    getRulesSync(entity: string, mode = 1): ValidationRulesMap | null {
        const key = `${entity}:${mode}`;
        const memHit = this.cache.get(key);
        if (memHit) return memHit;
        const lsHit = this.readLocalStorage(key);
        if (lsHit) {
            this.cache.set(key, lsHit);
            return lsHit;
        }
        return null;
    }

    async getRules(entity: string, mode = 1): Promise<ValidationRulesMap> {
        const key = `${entity}:${mode}`;

        const memHit = this.cache.get(key);
        if (memHit) return memHit;

        const lsHit = this.readLocalStorage(key);
        if (lsHit) {
            this.cache.set(key, lsHit);
            return lsHit;
        }

        const url = `${AppConsts.remoteServiceBaseUrl}/api/services/app/ValidationRule/GetRulesForEntity`;
        const params = new HttpParams().set('entity', entity).set('mode', String(mode));
        const result = await firstValueFrom(
            this.http.get<{ result?: ValidationRulesMap } | ValidationRulesMap>(url, { params })
        );
        const map = ((result as any)?.result ?? result ?? {}) as ValidationRulesMap;

        this.cache.set(key, map);
        this.writeLocalStorage(key, map);
        return map;
    }

    invalidate(entity?: string): void {
        if (!entity) {
            this.cache.clear();
            this.clearLocalStorage();
            return;
        }
        for (const key of Array.from(this.cache.keys())) {
            if (key.startsWith(`${entity}:`)) this.cache.delete(key);
        }
        for (const lsKey of this.listLocalStorageKeys()) {
            if (lsKey.startsWith(`${LS_PREFIX}${entity}:`)) {
                try { localStorage.removeItem(lsKey); } catch {}
            }
        }
    }

    clearAll(): void {
        this.cache.clear();
        this.clearLocalStorage();
    }

    private readLocalStorage(key: string): ValidationRulesMap | null {
        try {
            const raw = localStorage.getItem(LS_PREFIX + key);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object' || typeof parsed.ts !== 'number') {
                localStorage.removeItem(LS_PREFIX + key);
                return null;
            }
            if (Date.now() - parsed.ts > LS_TTL_MS) {
                localStorage.removeItem(LS_PREFIX + key);
                return null;
            }
            return parsed.data as ValidationRulesMap;
        } catch {
            return null;
        }
    }

    private writeLocalStorage(key: string, map: ValidationRulesMap): void {
        try {
            localStorage.setItem(LS_PREFIX + key, JSON.stringify({ ts: Date.now(), data: map }));
        } catch {
            // localStorage may be full or disabled — silently ignore
        }
    }

    private clearLocalStorage(): void {
        for (const k of this.listLocalStorageKeys()) {
            try { localStorage.removeItem(k); } catch {}
        }
    }

    private listLocalStorageKeys(): string[] {
        const keys: string[] = [];
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(LS_PREFIX)) keys.push(k);
            }
        } catch {}
        return keys;
    }
}
